import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  Field,
  Input,
  Spinner,
  Textarea,
  Button,
  makeStyles,
  shorthands,
  tokens,
  useRestoreFocusSource,
  DrawerFooter,
  Switch,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { useNotify } from "../../../shared/components/SuperToast";

import "../../../styles/animations.css";

// Business logic
import { useSelectedCiRelationTypesContext } from "../context/SelectedCiRelationTypesContext";
import { useCiRelationTypeQuery } from "../hooks/useCiRelationTypeQueries";
import { useCreateCiRelationType, useUpdateCiRelationType } from "../hooks/useCiRelationTypeMutations";
import { ciRelationTypeSchema } from "@shared/schemas";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";

const useStyles = makeStyles({
  root: {
    overflow: "visible",
    display: "flex",
    backgroundColor: "#fff",
    zIndex: "1000",
  },
  content: {
    flex: "1",
    padding: "16px",
    display: "grid",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gridRowGap: tokens.spacingVerticalXXL,
    gridAutoRows: "max-content",
  },
  field: {
    display: "grid",
    gridRowGap: "4px",
    margin: "6px 0",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap("1rem"),
  },
});

function CiRelationTypeDrawer({ isOpen, setIsOpen, newItem }) {
  const restoreFocusSourceAttributes = useRestoreFocusSource();
  const { t } = useTranslation();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [editingItem, setEditingItem] = useState(null);
  const { value: selectedItems } = useSelectedCiRelationTypesContext();
  const { notify } = useNotify();

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateItem = useItemValidation(ciRelationTypeSchema.$defs.ciRelationType);

  // Errors
  const [errors, setErrors] = useState([]);

  const publicId = selectedItems?.[0]?.public_id ?? null;

  // 1. Query and mutations
  const { data, error, isError, isLoading, isSuccess } = useCiRelationTypeQuery(publicId, isOpen && !newItem);

  const {
    mutate: createCiRelationType,
    isPending: isCreating,
    error: errorCreating,
    reset: resetCreating,
  } = useCreateCiRelationType(
    "ci_relation_types",
    (data) => {
      notify(t("item.messages.created", { name: data.name }), "success");
      handleClose();
    },
    null,
    false,
  );

  const {
    mutate: updateCiRelationType,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useUpdateCiRelationType(
    "ci_relation_types",
    (data) => {
      notify(t("item.messages.saved", { name: data.name }), "success");
      handleClose();
    },
    null,
  );

  // 2. Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (errorCreating) {
      newErrors.push({ message: errorCreating?.message || t("generic.error"), type: "error", details: errorCreating });
    }
    if (errorUpdating) {
      newErrors.push({ message: errorUpdating?.message || t("generic.error"), type: "error", details: errorUpdating });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, errorCreating, errorUpdating, t]);

  useEffect(() => {
    if (!isOpen) return;

    if (newItem) {
      const initialItem = {
        id: 0,
        code: "",
        name: "",
        reverse_name: "",
        bidirectional: 0,
        description: "",
      };
      setEditingItem(initialItem);
      setValidationErrors(validateItem(initialItem));
    } else if (data && isSuccess) setEditingItem(data);
    else setEditingItem(null);
  }, [isOpen, data, newItem, isSuccess]);

  useEffect(() => {
    if (isOpen && isError) {
      notify(error.message, "error");
      handleClose();
    }
  }, [isOpen, isError]);

  // 3. Handlers
  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["ci_relation_type", publicId] });
    setEditingItem(null);
    setValidationErrors({});
    setErrors([]);
    resetCreating();
    resetUpdating();
    setIsOpen(false);
  };

  const handleOpenChange = (_, data) => {
    if (data.type !== "backdropClick") setIsOpen(data.open);
  };

  const updateField = (field, value) => {
    const updated = { ...editingItem, [field]: value };
    setEditingItem(updated);
    setValidationErrors(validateItem(updated));
  };

  return (
    <div className={styles.root}>
      <Drawer
        {...restoreFocusSourceAttributes}
        type="overlay"
        position="end"
        separator
        size="medium"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={() => setIsOpen(false)}
              />
            }
          >
            {newItem
              ? t("ciRelationType.new", { defaultValue: "New relation type" })
              : editingItem?.name && editingItem?.reverse_name
                ? `${editingItem.name} / ${editingItem.reverse_name}`
                : (editingItem?.name ?? "")}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <hr />
        {!newItem && isLoading && (
          <div
            className="blur-overlay"
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Spinner size="medium" />
          </div>
        )}
        <DrawerBody>
          <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
          <div style={{ display: "flex", gap: "10px" }}>
            <Field
              className={styles.field}
              label={t("ciRelationType.code", { defaultValue: "Code" })}
              validationState={validationErrors.code ? "error" : undefined}
              required
            >
              <Input
                id="code"
                maxLength={25}
                value={editingItem?.code ?? ""}
                style={{ width: "120px" }}
                onChange={(e) => updateField("code", e.target.value)}
              />
            </Field>
            <Field
              className={styles.field}
              label={t("ciRelationType.bidirectional", { defaultValue: "Bidirectional" })}
            >
              <Switch
                checked={!!editingItem?.bidirectional}
                onChange={(_, data) => updateField("bidirectional", data.checked ? 1 : 0)}
              />
            </Field>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <Field
              className={styles.field}
              label={t("ciRelationType.name", { defaultValue: "Name" })}
              validationState={validationErrors.name ? "error" : undefined}
              required
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input
                id="name"
                maxLength={50}
                value={editingItem?.name ?? ""}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </Field>
            <Field
              className={styles.field}
              label={t("ciRelationType.reverse_name", { defaultValue: "Reverse name" })}
              validationState={validationErrors.reverse_name ? "error" : undefined}
              required
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input
                id="reverse_name"
                maxLength={50}
                value={editingItem?.reverse_name ?? ""}
                onChange={(e) => updateField("reverse_name", e.target.value)}
              />
            </Field>
          </div>
          <Field
            className={styles.field}
            label={t("generic.description")}
            validationState={validationErrors.description ? "error" : undefined}
          >
            <Textarea
              id="description"
              maxLength={255}
              value={editingItem?.description ?? ""}
              onChange={(e) => updateField("description", e.target.value)}
            />
          </Field>
          {validationErrors && Object.keys(validationErrors).length > 0 && (
            <DataValidationMessageBar validationErrors={validationErrors} />
          )}
        </DrawerBody>
        <DrawerFooter>
          <div className={styles.footerButtons}>
            <Button
              icon={isCreating || isUpdating ? <Spinner size="tiny" /> : undefined}
              appearance="primary"
              disabled={isCreating || isUpdating || Object.keys(validationErrors).length > 0}
              onClick={() => (newItem ? createCiRelationType(editingItem) : updateCiRelationType(editingItem))}
            >
              {newItem ? t("generic.create") : t("generic.update")}
            </Button>
            <Button onClick={handleClose}>{t("generic.cancel")}</Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </div>
  );
}

export default CiRelationTypeDrawer;
